// Implements: F-MCK-001, F-UIX-023, F-UIX-009
/**
 * Creating a mockup with its first turn (7.6), as one action that never rejects.
 *
 * It answers where the new mockup and its first run are, or the refusal itself, because the
 * container that awaits it has one decision left: go there, or say why not.
 *
 * Two rules, both about money. A 2xx IS a creation: if the answer carries no usable
 * `mockup_id` (empty, not JSON, no id), it is still `ok`, with `mockupId` `null`, because
 * reporting it as a failure would invite a second paid create. And the gallery refresh is not
 * awaited: the person is taken to the mockup now, and the list catches up on its own time.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { GALLERY_COUNTS_KEY, MOCKUPS_KEY } from "../../../shared/api/queryKeys";
import { createMockup } from "../api/mockupsApi";
import type { CreateMockupInput } from "../api/mockupsApi";

export type CreateMockupOutcome =
  | {
      readonly ok: true;
      readonly mockupId: string | null;
      /** The run the first turn will be recorded under, when the answer names one. */
      readonly generationId: string | null;
    }
  | { readonly ok: false; readonly error: ApiError };

function field(body: unknown, name: string): string | null {
  if (typeof body !== "object" || body === null) return null;
  const value = (body as Record<string, unknown>)[name];
  return typeof value === "string" && value !== "" ? value : null;
}

export function useCreateMockup(): (input: CreateMockupInput) => Promise<CreateMockupOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (input: CreateMockupInput): Promise<CreateMockupOutcome> => {
      let created: unknown;
      try {
        created = await createMockup(input);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      void queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY });
      // Mockup v3 P8: a new mockup changes the gallery's tab counts too.
      void queryClient.invalidateQueries({ queryKey: GALLERY_COUNTS_KEY });
      return { ok: true, mockupId: field(created, "mockup_id"), generationId: field(created, "generation_id") };
    },
    [queryClient],
  );
}
